import { z } from 'zod';
import { isoDateSchema, moneySchema, nonNegativeMoneySchema, positiveMoneySchema, shortTextSchema, uuidSchema } from '../primitives.js';

/**
 * Where the business's money is held: cash at a location, a bank account, a
 * mobile money wallet.
 *
 * A balance is the account's opening balance plus every movement attributed to
 * it since — the one it names, else its location's default account for the
 * method's kind. Unpaid amounts, credit, write-offs, adjustments and waivers
 * move no money and never appear. Movements no account claims yet are shown as
 * unassigned rather than quietly dropped.
 */

export const MONEY_ACCOUNT_KINDS = ['cash', 'bank', 'mobile_money', 'other'] as const;
export type MoneyAccountKind = (typeof MONEY_ACCOUNT_KINDS)[number];

export const MONEY_ACCOUNT_KIND_LABELS: Record<MoneyAccountKind, string> = {
  cash: 'Cash',
  bank: 'Bank account',
  mobile_money: 'Mobile money',
  other: 'Other',
};

export const moneyAccountInputSchema = z.object({
  name: shortTextSchema,
  kind: z.enum(MONEY_ACCOUNT_KINDS),
  branchId: uuidSchema.nullable().optional(),
  /** Receives the location's movements of this kind that name no account. */
  isDefault: z.boolean().optional(),
  openingBalance: nonNegativeMoneySchema.optional(),
  openingOn: isoDateSchema.optional(),
  note: z.string().trim().max(500).optional(),
  /** The worker whose hands this cash is in: a member of the business, or a name. */
  holderUserId: uuidSchema.nullable().optional(),
  holderName: shortTextSchema.nullable().optional(),
});
export type MoneyAccountInput = z.infer<typeof moneyAccountInputSchema>;

export const moneyAccountUpdateSchema = z
  .object({
    name: shortTextSchema.optional(),
    isDefault: z.boolean().optional(),
    archived: z.boolean().optional(),
    holderUserId: uuidSchema.nullable().optional(),
    holderName: shortTextSchema.nullable().optional(),
    /**
     * What the account held at the start of `openingOn`, from its bank
     * statement or a cash count. Movements before that day are inside it and
     * not counted again. Changing either moves the balance on every day since,
     * so it needs a reason, kept in the audit trail.
     */
    openingBalance: moneySchema.optional(),
    openingOn: isoDateSchema.nullable().optional(),
    reason: z.string().trim().min(3).max(500).optional(),
  })
  .refine((v) => (v.openingBalance === undefined && v.openingOn === undefined) || Boolean(v.reason), {
    message: 'Say why the opening balance or date is changing',
    path: ['reason'],
  });
export type MoneyAccountUpdate = z.infer<typeof moneyAccountUpdateSchema>;

export const moneyTransferInputSchema = z
  .object({
    fromAccountId: uuidSchema,
    toAccountId: uuidSchema,
    amount: positiveMoneySchema,
    transferredOn: isoDateSchema,
    reference: shortTextSchema.optional(),
    note: z.string().trim().max(500).optional(),
    idempotencyKey: z.string().trim().min(8).max(100),
    /**
     * The money has left the source but the destination has not confirmed it
     * yet — cash handed over to be banked. It reaches the destination only
     * when completed. Leave false when it has already arrived.
     */
    pending: z.boolean().optional(),
    /** Who carried or handled the money. */
    handledByName: shortTextSchema.optional(),
    /** The deposit slip or transfer receipt, already uploaded. */
    evidenceAttachmentId: uuidSchema.optional(),
    /**
     * The member who physically handed the money over, when somebody else is
     * recording it (an accountant entering a worker's handover). Omitted: the
     * person recording it handed it over.
     */
    submittedByUserId: uuidSchema.optional(),
    /**
     * A pending handover or deposit of the same amount, between the same two
     * accounts, on the same day, already waits for confirmation. Refused as a
     * likely duplicate unless the person confirms it is a second one.
     */
    confirmDuplicate: z.boolean().optional(),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'Choose two different accounts',
    path: ['toAccountId'],
  });
export type MoneyTransferInput = z.infer<typeof moneyTransferInputSchema>;

/** The destination confirms a pending transfer arrived — on the day it did. */
export const moneyTransferCompleteSchema = z.object({
  completedOn: isoDateSchema,
  /**
   * The reference the money arrived under: the teller number or statement
   * line of a deposit. Required when it lands in a bank or wallet account, so
   * the deposit can be found on the bank statement; one reference backs one
   * arrival in an account.
   */
  reference: shortTextSchema.optional(),
});
export type MoneyTransferComplete = z.infer<typeof moneyTransferCompleteSchema>;

export const moneyAccountViewSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: z.enum(MONEY_ACCOUNT_KINDS),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  currencyCode: z.string(),
  isDefault: z.boolean(),
  archived: z.boolean(),
  openingBalance: moneySchema,
  openingOn: z.string().nullable(),
  holderUserId: uuidSchema.nullable(),
  /** The worker holding this account's cash, by member name or as typed. */
  holderName: z.string().nullable(),
  /** Opening balance plus every movement attributed to it up to `asOf`. */
  balance: moneySchema,
  inflow: moneySchema,
  outflow: moneySchema,
});
export type MoneyAccountView = z.infer<typeof moneyAccountViewSchema>;

export interface MoneyAccounts {
  asOf: string;
  currencyCode: string;
  accounts: MoneyAccountView[];
  /** Movements of each kind that no account claims: no account named, no default set. */
  unassigned: { cash: string; bank: string };
}

export const moneyMovementViewSchema = z.object({
  on: z.string(),
  source: z.string(),
  ref: z.string(),
  amount: moneySchema,
  kind: z.string(),
});
export type MoneyMovementView = z.infer<typeof moneyMovementViewSchema>;

export interface MoneyTransferView {
  id: string;
  fromAccountId: string;
  fromAccountName: string;
  toAccountId: string;
  toAccountName: string;
  amount: string;
  transferredOn: string;
  reference: string | null;
  note: string | null;
  recordedBy: string | null;
  voided: boolean;
  voidReason: string | null;
  status: 'pending' | 'completed';
  completedOn: string | null;
  completedBy: string | null;
  handledByName: string | null;
  evidenceAttachmentId: string | null;
  /**
   * cash → bank is a deposit, bank → cash a withdrawal, cash → cash a
   * handover (custody changing hands), anything else a transfer.
   */
  kind: 'deposit' | 'withdrawal' | 'handover' | 'transfer';
  /** Who handed the money over: the member named, else whoever recorded it. */
  submittedBy: string | null;
  /** Who is to receive it: the destination account's holder, when it has one. */
  receiverName: string | null;
  receiverUserId: string | null;
  /** The bank's or receiver's reference for its arrival, given on completion. */
  completionReference: string | null;
  /** Whether the person asking may confirm this pending transfer arrived. */
  mayConfirm: boolean;
  /** Whether the person asking may void it (or, while it waits, decline or take it back). */
  mayCancel: boolean;
}

// ── The Cash & Bank dashboard ──────────────────────────────────────────────

export const cashBankQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    country: z.string().trim().length(2).optional(),
    state: z.string().trim().min(1).max(40).optional(),
    branchId: uuidSchema.optional(),
    accountId: uuidSchema.optional(),
    /** A worker: a member's user id. */
    holderUserId: uuidSchema.optional(),
  })
  .refine((v) => v.from <= v.to, { message: 'The period must not end before it starts', path: ['to'] });
export type CashBankQuery = z.infer<typeof cashBankQuerySchema>;

// ── Transaction history ────────────────────────────────────────────────────

/** Ledgers a transaction can come through: the movement sources, as movements.ts names them. */
export const CASH_BANK_TRANSACTION_TYPES = [
  'sale',
  'sale_void',
  'refund',
  'expense',
  'expense_reversal',
  'opening_balance_payment',
  'loan',
  'remittance',
  'hire_purchase',
  'settlement',
  'transfer',
  'rider_return',
  'rider_payout',
  'rider_collection',
  'investor_payout',
  'investment_payment',
] as const;
export type CashBankTransactionType = (typeof CASH_BANK_TRANSACTION_TYPES)[number];

export const cashBankTransactionsQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    country: z.string().trim().length(2).optional(),
    state: z.string().trim().min(1).max(40).optional(),
    branchId: uuidSchema.optional(),
    accountId: uuidSchema.optional(),
    holderUserId: uuidSchema.optional(),
    /** Comma-separated ledgers; all when absent. */
    types: z
      .string()
      .trim()
      .transform((s) => s.split(',').map((x) => x.trim()).filter(Boolean))
      .pipe(z.array(z.enum(CASH_BANK_TRANSACTION_TYPES)).max(CASH_BANK_TRANSACTION_TYPES.length))
      .optional(),
    kind: z.enum(['cash', 'bank']).optional(),
    direction: z.enum(['in', 'out', 'transfer']).optional(),
    /** Matches description, person, rider, reference, account, location or who recorded it. */
    q: z.string().trim().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(100),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .refine((v) => v.from <= v.to, { message: 'The period must not end before it starts', path: ['to'] });
export type CashBankTransactionsQuery = z.infer<typeof cashBankTransactionsQuerySchema>;

/**
 * One money movement as the owner reads it. A transfer between the company's
 * own accounts is ONE row, from one account to the other — never income or an
 * expense. Every other row is one movement in or out of one account (or of no
 * account yet), exactly as the balances count it.
 */
export interface CashBankTransaction {
  /** Unique within the response. */
  id: string;
  type: CashBankTransactionType;
  /** The record behind it: a sale, expense, loan entry, receipt, transfer… */
  ref: string;
  /** The day the money moved. */
  on: string;
  /** When it was paid or recorded, when that was on the same day. */
  at: string | null;
  /** A loan entry's kind: disbursement, top_up or repayment. */
  subtype: string | null;
  /** A loan entry that undoes an earlier one. */
  reversal: boolean;
  description: string | null;
  /** The customer, payee, borrower, investor or worker. */
  person: string | null;
  rider: string | null;
  reference: string | null;
  /** Signed for movements (+ in, − out); positive for a transfer. */
  amount: string;
  direction: 'in' | 'out' | 'transfer';
  /** cash or bank: the kind of money (for a transfer, of the destination). */
  kind: 'cash' | 'bank';
  method: string | null;
  /** The account it landed in or left; null when no account claims it yet. For a transfer, the destination. */
  accountId: string | null;
  accountName: string | null;
  /** A transfer's source account. */
  fromAccountId: string | null;
  fromAccountName: string | null;
  branchName: string | null;
  recordedBy: string | null;
  /** A transfer's state: completed, or pending until the receiver confirms. */
  status: 'pending' | 'completed' | null;
  note: string | null;
}

export interface CashBankTransactions {
  from: string;
  to: string;
  currencyCode: string;
  /** Matching rows, before paging. */
  count: number;
  /** Over every matching row. Transfers move money between the company's own accounts: never in or out. */
  totals: { moneyIn: string; moneyOut: string; transfers: string };
  rows: CashBankTransaction[];
}

export interface CashBankMovement {
  on: string;
  source: string;
  ref: string;
  amount: string;
  kind: string;
  accountId: string | null;
  accountName: string | null;
  branchName: string | null;
}

/**
 * One balance across a period: what it was the day before the period began,
 * what came in and went out during it, and what it was at its end.
 *
 * opening + moneyIn − moneyOut + transfersIn − transfersOut = closing.
 * `opening` and `closing` are each worked out on their own, by the same rules
 * as the balances shown for those days, so the identity is checked rather than
 * assumed: `difference` is what is left over and should always be 0.00. It is
 * shown, never hidden, when it is not.
 */
export interface CashBankPeriodFigures {
  /** At the end of the day before the period (`openingAsOf`). */
  opening: string;
  /** Received during the period, from outside the company's own accounts. */
  moneyIn: string;
  /** Paid out during the period, to outside the company's own accounts. */
  moneyOut: string;
  /** Moved in from the company's own accounts: neither income nor money from outside. */
  transfersIn: string;
  /** Moved out to the company's own accounts. Cash sent and not yet confirmed has not left. */
  transfersOut: string;
  /** At the end of the period's last day. */
  closing: string;
  /** closing − (opening + moneyIn − moneyOut + transfersIn − transfersOut). */
  difference: string;
}

/** An account as Cash & Bank shows it for a period. */
export type CashBankAccountView = MoneyAccountView & { period: CashBankPeriodFigures };

export interface CashBankDashboard {
  from: string;
  to: string;
  /** Balances are as of the end of `to`. */
  asOf: string;
  /**
   * The period's cash and bank, as the Total Cash and Total Bank panels show
   * them: `closing` is each panel's total as of `to`, `opening` the same panel
   * as of `openingAsOf`, the day before `from`.
   */
  summary: {
    openingAsOf: string;
    cash: CashBankPeriodFigures;
    bank: CashBankPeriodFigures;
  };
  currencyCode: string;
  totals: {
    /** Physical cash: every cash account's balance. */
    cash: string;
    /** Bank and wallet accounts. */
    bank: string;
    /** cash + bank: money the company holds in its accounts. */
    available: string;
    /** Left a cash account, not yet confirmed at the destination. Not in `available`. */
    depositsInTransit: string;
  };
  /** Each account, grouped for "who holds what", with its own figures for the period. */
  accounts: Array<CashBankAccountView>;
  /** Cash a rider is confirmed to hold (recorded collections less returns, refunds and merchant payments). */
  riderHeld: {
    confirmed: string;
    riders: Array<{ riderId: string; riderName: string; amount: string }>;
    /** Payments recorded as with the rider ("Cash to Rider", synced cash) — kept out of the till, not confirmed. */
    unconfirmed: string;
    /**
     * The same, per rider: what they took from customers in the period, the
     * part that is their own pay for those rides, and the company's part they
     * owe until it is settled (from their earnings, or handed over).
     */
    unconfirmedByRider: Array<{ riderId: string | null; riderName: string; rides: number; held: string; riderShare: string; companyShare: string }>;
  };
  /** Movements no account claims: no account named, no default for the location and kind. */
  unassigned: {
    cash: string;
    bank: string;
    bySource: Array<{ source: string; kind: string; branchName: string | null; count: number; amount: string }>;
  };
  /** Bank payments awaiting verification or rejected: never counted as funds. */
  unverified: { count: number; amount: string };
  /**
   * Expenses recorded but not yet paid (a recurring cost taken ahead of its
   * date, or created by its schedule). No account moves until the payment is
   * recorded. `due` are those whose date has come by the end of the period.
   */
  unpaidExpenses: { count: number; amount: string; due: { count: number; amount: string } };
  period: {
    /** Into the company's accounts; transfers between its own accounts are left out unless one account is chosen. */
    moneyIn: string;
    moneyOut: string;
    bySource: Array<{ source: string; moneyIn: string; moneyOut: string }>;
    expensesPaid: string;
    /** Paid expenses deleted in the period: the money came back on the day of deletion. */
    expensesReversed: string;
    loansDisbursed: string;
    loansRepaid: string;
    /**
     * What loan repayments received in the period paid off: principal returns
     * a receivable, interest and fees are income. Together, `loansRepaid` for
     * money lent (receivable loans).
     */
    loanRepayments: { principal: string; interest: string; fees: string };
    riderPayouts: string;
    investorPayouts: string;
    /**
     * Remittance received in the period, split as each payment was: the
     * company's revenue, and the investors' share collected for them — owed to
     * them, never revenue.
     */
    remittance: { company: string; investors: string };
  };
  /** Money received from customers, riders and borrowers in the period. Transfers and handovers are not income. */
  collections: CashBankCollections;
  /** Physical cash, by location and by who holds it, at the end of the period. */
  cashByLocation: CashByLocation[];
  /** Handovers and deposits waiting for the receiver or the bank to confirm, as of now. */
  awaitingConfirmation: MoneyTransferView[];
  /**
   * What the money in the accounts is: owed out of it, and owed to the company
   * but not in hand. The whole business only — null when the view is narrowed
   * to a location, an account or a holder, or the caller cannot see every
   * location.
   */
  position: CashBankPosition | null;
  deposits: {
    pending: { count: number; amount: string };
    completed: { count: number; amount: string };
  };
  recent: CashBankMovement[];
}

// ── Collections, custody and position ──────────────────────────────────────

/** Sources that bring money in from outside the company: income and repayments, never a transfer. */
export const COLLECTION_SOURCES = ['sale', 'remittance', 'hire_purchase', 'opening_balance_payment', 'loan', 'rider_return'] as const;

export interface CashBankCollectionGroup {
  key: string;
  label: string;
  amount: string;
  count: number;
}

export interface CashBankCollections {
  total: string;
  count: number;
  /** cash, bank_transfer, pos… as recorded. */
  byMethod: CashBankCollectionGroup[];
  /** Where it was received; '' is a movement with no location. */
  byLocation: CashBankCollectionGroup[];
  /** The member who recorded receiving it; '' is nobody recorded. */
  byWorker: CashBankCollectionGroup[];
  /** cash or bank (anything electronic). */
  byKind: CashBankCollectionGroup[];
  /** Which ledger it came through: sale, remittance, loan… */
  bySource: CashBankCollectionGroup[];
}

export interface CashByLocation {
  /** Null: business-wide cash accounts, and cash recorded with no location. */
  branchId: string | null;
  branchName: string | null;
  /** In cash accounts at this location. */
  inAccounts: string;
  /** Cash recorded at this location that no account claims yet (no default cash account). */
  unassigned: string;
  /** Sent from here, not confirmed by the receiver yet: out of this location, not anywhere else yet. */
  sentAwaitingConfirmation: string;
  total: string;
  holders: Array<{ accountId: string; accountName: string; holderUserId: string | null; holderName: string | null; balance: string }>;
}

/** As of the end of the period. */
export interface CashBankPosition {
  /** Cash and bank in accounts, plus money received that no account claims yet. */
  held: string;
  /** Deposits and handovers on their way: left one account, not confirmed in the other. */
  inTransit: string;
  /** Collected for investors and not yet paid to them: a payable, never the company's. */
  investorPayable: string;
  /** Principal borrowed and still owed to lenders. */
  loansPayable: { principal: string; count: number };
  /** Expenses recorded and due by the end of the period, not paid yet. */
  unpaidExpensesDue: string;
  /** held + inTransit − investorPayable − loansPayable − unpaidExpensesDue. Can be negative. */
  companyOwn: string;
  /** Owed to the company, not in hand: principal lent and still owed. Interest is income only when received. */
  loansReceivable: { principal: string; count: number };
  /** Fares riders are confirmed to hold, not handed over yet. */
  riderHeld: string;
}

// ── Cash flow by period ────────────────────────────────────────────────────

export const CASH_FLOW_GRANULARITIES = ['day', 'week', 'month', 'year'] as const;
export type CashFlowGranularity = (typeof CASH_FLOW_GRANULARITIES)[number];

export const cashFlowQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: z.enum(CASH_FLOW_GRANULARITIES),
    country: z.string().trim().length(2).optional(),
    state: z.string().trim().min(1).max(40).optional(),
    branchId: uuidSchema.optional(),
  })
  .refine((v) => v.from <= v.to, { message: 'The period must not end before it starts', path: ['to'] });
export type CashFlowQuery = z.infer<typeof cashFlowQuerySchema>;

export interface CashFlowRow {
  /** First and last day of the bucket, clipped to the query. */
  from: string;
  to: string;
  /**
   * What moved the cash and bank in view during the bucket, as the Total Cash
   * and Total Bank panels count it: into or out of an account from its opening
   * date, or in no account at a location in view. Transfers between the
   * company's own accounts are left out.
   */
  moneyIn: string;
  moneyOut: string;
  /** moneyIn − moneyOut. */
  net: string;
  /**
   * Moved in from (or, negative, out to) the company's own accounts outside the
   * view: nothing for the whole business. Cash sent and not yet confirmed has not
   * left the cash it was sent from.
   */
  transfers: string;
  /** Income received: sales (net of voids and refunds), the company's share of remittance, loan interest and fees. */
  income: string;
  /** Expenses paid, net of paid expenses cancelled. */
  expenses: string;
  /** At the end of the bucket, as the Total Cash and Total Bank panels show it for that day. */
  cash: string;
  bank: string;
  /**
   * (cash + bank) − (the previous row's cash + bank, or the opening, + net + transfers).
   * Each balance is worked out on its own, so this is a check: always 0.00, shown if not.
   */
  difference: string;
  /** Lent and still owed at the end of the bucket (principal). */
  loansOutstanding: string;
}

export interface CashFlow {
  granularity: CashFlowGranularity;
  currencyCode: string;
  /** Cash and bank as the panels show them at the end of `asOf`, the day before the first row. */
  opening: { asOf: string; cash: string; bank: string };
  rows: CashFlowRow[];
  /** Weeks start on this weekday (0 = Sunday), the business's remittance week. */
  weekStartsOn: number;
}

// ── Bank statement checkpoints ─────────────────────────────────────────────

export const moneyStatementInputSchema = z.object({
  accountId: uuidSchema,
  /** The statement's closing date. */
  statementOn: isoDateSchema,
  /** The closing balance the bank's statement shows; an overdraft is negative. */
  closingBalance: moneySchema,
  /** The statement's number or the line it was read from. */
  reference: shortTextSchema,
  note: z.string().trim().max(500).optional(),
  attachmentId: uuidSchema.optional(),
});
export type MoneyStatementInput = z.infer<typeof moneyStatementInputSchema>;

export interface MoneyStatementView {
  id: string;
  accountId: string;
  accountName: string;
  statementOn: string;
  closingBalance: string;
  reference: string;
  note: string | null;
  attachmentId: string | null;
  recordedBy: string | null;
  createdAt: string;
  voided: boolean;
  voidReason: string | null;
  /** The account's balance in CenoX at the end of that day. */
  bookBalance: string;
  /** Deposits sent by that day and confirmed after it: on the books' way, not yet in the bank. */
  inTransit: string;
  /** closingBalance − bookBalance. Zero is reconciled; anything else is shown, never booked. */
  difference: string;
  reconciled: boolean;
}

// ── The journal: every movement as balanced entries ────────────────────────

export const moneyJournalQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    accountId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
  })
  .refine((v) => v.from <= v.to, { message: 'The period must not end before it starts', path: ['to'] });
export type MoneyJournalQuery = z.infer<typeof moneyJournalQuerySchema>;

export interface MoneyJournalLine {
  /** A money account's id, or a ledger heading such as 'revenue:sales'. */
  account: string;
  accountName: string;
  debit: string;
  credit: string;
}

export interface MoneyJournalEntry {
  on: string;
  source: string;
  /** The record the movement came from (sale, receipt, loan transaction, transfer…). */
  ref: string;
  lines: MoneyJournalLine[];
}

export interface MoneyJournal {
  from: string;
  to: string;
  currencyCode: string;
  entries: MoneyJournalEntry[];
  /** True when there were more entries than returned; the totals still cover all of them. */
  truncated: boolean;
  totals: { debit: string; credit: string; balanced: boolean };
  /** Each heading's debits and credits in the period. */
  trialBalance: Array<{ account: string; accountName: string; debit: string; credit: string }>;
}

// ── A worker's own custody ─────────────────────────────────────────────────

/** What a worker who hands over cash sees: their own cash, where it may go, and what waits. */
export interface MoneyCustody {
  currencyCode: string;
  /** Cash accounts the worker holds, with their balances. */
  held: Array<{ accountId: string; accountName: string; branchName: string | null; balance: string }>;
  /** Where they may send it: cash accounts other people hold, and bank or wallet accounts. Names only. */
  destinations: Array<{ accountId: string; accountName: string; kind: MoneyAccountKind; branchName: string | null; holderName: string | null }>;
  /** Pending handovers and deposits that concern them; `mayConfirm` marks the ones they confirm. */
  transfers: MoneyTransferView[];
}
